import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";

import { useAuth } from "../context/AuthContext";
import { useAppContext } from "../context/AppContext";
import NotificationForm from "./NotificationForm";
import type { PwaNotificationSettings } from "../types/pwaNotifications";
import { defaultPwaNotificationSettings, normalizePwaNotificationSettings } from "../types/pwaNotifications";

const PwaForm = () => {
  const { authorizedFetch } = useAuth();
  const { pwaNotifications, refreshPwaNotifications } = useAppContext();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationSaving, setNotificationSaving] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState<{ saved: boolean; error: string | null }>({
    saved: false,
    error: null
  });
  const [notificationInitial, setNotificationInitial] = useState<PwaNotificationSettings>(defaultPwaNotificationSettings);
  const [notificationDraft, setNotificationDraft] = useState<PwaNotificationSettings>(defaultPwaNotificationSettings);

  const hasNotificationChanges = useMemo(() => {
    return (
      notificationDraft.streamOnline !== notificationInitial.streamOnline ||
      notificationDraft.streamOffline !== notificationInitial.streamOffline
    );
  }, [notificationDraft, notificationInitial]);

  const normalizeNotifications = (config?: Partial<{ notifications?: Partial<PwaNotificationSettings> }> | null) => {
    const source =
      (config?.notifications as Partial<PwaNotificationSettings> | undefined) ??
      (config as Partial<PwaNotificationSettings> | undefined);
    return normalizePwaNotificationSettings(source);
  };

  useEffect(() => {
    if (!notificationsOpen) {
      return;
    }

    let cancelled = false;
    const loadNotifications = async () => {
      setNotificationLoading(true);
      setNotificationStatus({ saved: false, error: null });
      try {
        const latest = await refreshPwaNotifications();
        if (!latest) {
          throw new Error("Failed to load notification preferences");
        }

        if (!cancelled) {
          setNotificationInitial(latest);
          setNotificationDraft(latest);
        }
      } catch (error) {
        if (!cancelled) {
          setNotificationStatus({
            saved: false,
            error: error instanceof Error ? error.message : "Unable to load notification preferences"
          });
        }
      } finally {
        if (!cancelled) {
          setNotificationLoading(false);
        }
      }
    };

    void loadNotifications();

    return () => {
      cancelled = true;
    };
  }, [notificationsOpen, refreshPwaNotifications]);

  useEffect(() => {
    if (notificationsOpen) {
      return;
    }
    setNotificationInitial(pwaNotifications);
    setNotificationDraft(pwaNotifications);
  }, [notificationsOpen, pwaNotifications]);

  const handleNotificationChange = (key: keyof PwaNotificationSettings, value: boolean) => {
    setNotificationDraft((prev) => {
      if (prev[key] === value) {
        return prev;
      }
      const next = { ...prev, [key]: value };
      setNotificationStatus((status) => (status.error || status.saved ? { saved: false, error: null } : status));
      return next;
    });
  };

  const handleSaveNotifications = async () => {
    setNotificationSaving(true);
    setNotificationStatus({ saved: false, error: null });
    try {
      const response = await authorizedFetch("/api/config/pwa", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          notifications: {
            streamOnline: notificationDraft.streamOnline,
            streamOffline: notificationDraft.streamOffline
          }
        })
      });

      const payload = (await response.json().catch(() => null)) as {
        config?: Partial<{ notifications?: Partial<PwaNotificationSettings> }>;
        error?: string;
      } | null;

      if (!response.ok || !payload?.config) {
        throw new Error(payload?.error ?? "Failed to save notification preferences");
      }

      const normalized = normalizeNotifications(payload.config);
      setNotificationInitial(normalized);
      setNotificationDraft(normalized);
      await refreshPwaNotifications(normalized);
      setNotificationStatus({ saved: true, error: null });
      setNotificationsOpen(false);
    } catch (error) {
      setNotificationStatus({
        saved: false,
        error: error instanceof Error ? error.message : "Unable to save notification preferences"
      });
    } finally {
      setNotificationSaving(false);
    }
  };

  const handleCloseNotifications = () => {
    setNotificationsOpen(false);
    setNotificationDraft(notificationInitial);
    setNotificationStatus({ saved: false, error: null });
  };

  return (
    <Stack spacing={3}>
      <Stack spacing={1.5}>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Configure push notification defaults for installed PWAs">
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Configure notifications
            </Typography>
          </Tooltip>
          <Tooltip title="Press to manage push notification preferences for the PWA">
            <span>
              <IconButton
                aria-label="Configure notifications"
                onClick={() => {
                  setNotificationsOpen(true);
                }}
                size="small"
              >
                <NotificationsActiveIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Stack>
      <Dialog open={notificationsOpen} onClose={handleCloseNotifications} maxWidth="xs" fullWidth>
        <DialogTitle>Notification preferences</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {notificationLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <>
                {notificationStatus.error && <Alert severity="error">{notificationStatus.error}</Alert>}
                {notificationStatus.saved && !hasNotificationChanges && (
                  <Alert severity="success">Notification preferences updated</Alert>
                )}
                <NotificationForm
                  values={notificationDraft}
                  onChange={handleNotificationChange}
                  disabled={notificationSaving}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNotifications} disabled={notificationSaving}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handleSaveNotifications();
            }}
            disabled={notificationLoading || notificationSaving || !hasNotificationChanges}
          >
            {notificationSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default PwaForm;
